import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import HourPresetInput from "./hour-preset-input";
import {
  ColorsConstants,
  borderColors,
  inputColor,
} from "../cssUtils/constants";
import typography from "../cssUtils/typography";
import {
  labelSx,
  sectionLabelSx,
} from "../components/ChatbotFlow/utils";

// Live Agent settings shared by the Fallback Autoresponder drawer and the Nodebot
// Live Agent node panel: notification pickers, end-chat message and unassigned
// queue cleanup.

// Multiline-capable text field style (the shared one in ChatbotFlow/utils hardcodes
// height: 40 for single-line inputs).
export const liveAgentTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    color: inputColor,
    ...typography.bodyLarge,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColors.inputFieldGrey,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: ColorsConstants.primaryGray50,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ColorsConstants.primaryGray100,
    },
  },
  "& textarea, & input": {
    padding: "8px 14px",
    ...typography.bodyLarge,
    color: inputColor,
  },
};

// Only templates with exactly one variable and no header can carry the "sender name +
// phone" notification payload sent from enable_live_chat (whatsapp/handler_utils.py).
export const filterValidTemplates = (templates) =>
  templates.filter((t) => {
    const matches = t?.template_json?.content?.match(/{{\d+}}/g) || [];
    const hasOneVariable = new Set(matches).size === 1;
    const isHeaderNone = t?.template_preview_json?.headerType === "none";
    return hasOneVariable && isHeaderNone;
  });

// kind: "email" lists users with an email, "whatsapp" users with a phone number.
export const NotificationUsersSelect = ({
  kind,
  users,
  value,
  onChange,
  error,
}) => {
  const isEmail = kind === "email";

  return (
    <Autocomplete
      multiple
      options={users.filter((u) => (isEmail ? u.email : u.dial_code))}
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(option) => option.name || ""}
      renderOption={(renderProps, option) => (
        <Box component="li" {...renderProps} key={option.id}>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            <Typography variant="caption" color="gray">
              {isEmail
                ? option.email
                : `+${option.country_code} ${option.dial_code}`}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            label={option.name}
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={isEmail ? "Select users (Email)" : "Select users (WhatsApp)"}
          error={!!error}
          helperText={error}
          sx={liveAgentTextFieldSx}
        />
      )}
    />
  );
};

export const NotificationTemplateSelect = ({
  templates,
  value,
  onChange,
  error,
}) => (
  <Box>
    <Typography sx={{ ...typography.bodySmall, mb: 1 }}>
      Select WhatsApp Template
    </Typography>
    <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mb: 1 }}>
      Only templates with exactly 1 variable (e.g. {"{{1}}"}) are shown.
    </Typography>
    <Autocomplete
      options={filterValidTemplates(templates)}
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      isOptionEqualToValue={(o, v) => o.template_id === v.template_id}
      getOptionLabel={(option) => option.template_name || ""}
      renderOption={(renderProps, option) => (
        <Box component="li" {...renderProps} key={option.template_id}>
          <Box>
            <Typography variant="body2">{option.template_name}</Typography>
            <Typography variant="caption" color="gray">
              {option.template_json?.content}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Select template"
          error={!!error}
          helperText={error}
          sx={liveAgentTextFieldSx}
        />
      )}
    />
  </Box>
);

// `compact` drops the divider and section title, for a section that already has its
// own header (the Live Agent node's override sections).
export const EndChatFields = ({
  mode,
  message,
  onModeChange,
  onMessageChange,
  error,
  compact = false,
}) => (
  <>
    {!compact && (
      <>
        <Divider sx={{ mb: 3 }} />
        <Typography sx={sectionLabelSx}>On end chat by support agent</Typography>
      </>
    )}

    <FormControl sx={{ mb: 1, width: "100%" }}>
      <RadioGroup value={mode} onChange={(e) => onModeChange(e.target.value)}>
        <FormControlLabel
          value="SEND_MESSAGE"
          control={<Radio />}
          label={
            <Typography sx={typography.bodyLarge}>
              Send auto-reply to contact before closing session
            </Typography>
          }
        />
        <FormControlLabel
          value="CLOSE_IMMEDIATELY"
          control={<Radio />}
          label={
            <Typography sx={typography.bodyLarge}>
              Close session immediately — no message sent
            </Typography>
          }
        />
      </RadioGroup>
    </FormControl>

    {mode === "SEND_MESSAGE" && (
      <Box sx={{ mb: compact ? 1 : 3 }}>
        <Typography sx={labelSx}>End chat message</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          error={!!error}
          helperText={error || "Sent to the contact when the agent clicks End Chat."}
          sx={liveAgentTextFieldSx}
        />
      </Box>
    )}
  </>
);

export const liveAgentSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: ColorsConstants.primaryBlue100,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: ColorsConstants.primaryBlue100,
  },
};

// `compact` drops the divider and section title (see EndChatFields). `alwaysEnabled`
// drops the on/off switch and treats the cleanup as on, for the Live Agent node,
// where switching its override section on is what enables it.
export const QueueCleanupFields = ({
  enabled,
  hours,
  onEnabledChange,
  onHoursChange,
  error,
  compact = false,
  alwaysEnabled = false,
}) => (
  <>
    {!compact && <Divider sx={{ mb: 3 }} />}

    {/* The node's section (compact + alwaysEnabled) has its own header, which
        already says what it does, so skip this one there. */}
    {!(compact && alwaysEnabled) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box>
          {!compact && (
            <Typography sx={sectionLabelSx}>Unassigned queue cleanup</Typography>
          )}
          <Typography
            sx={{
              ...typography.bodySmall,
              color: ColorsConstants.primaryGray60,
            }}
          >
            Auto-close unassigned conversations with unread messages after a set
            time.
          </Typography>
        </Box>
        {!alwaysEnabled && (
          <Switch
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            sx={liveAgentSwitchSx}
          />
        )}
      </Box>
    )}

    {(enabled || alwaysEnabled) && (
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography sx={labelSx}>Clean up queue after</Typography>
        <HourPresetInput value={hours} onChange={onHoursChange} />
        {error && (
          <Typography
            sx={{ ...typography.bodySmall, color: "#b50000", mt: 0.5 }}
          >
            {error}
          </Typography>
        )}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "#FEF6E0",
            border: "1px solid #F5D98B",
          }}
        >
          <Typography sx={{ ...typography.bodySmall, color: "#8A6D1D" }}>
            ⚠ After {hours || 0} hours, unassigned conversations with unread
            messages will be automatically closed.
          </Typography>
        </Box>
      </Box>
    )}
  </>
);
